// Pure logic for Task Templates (increment 7): working-day offset math, template-task
// validation, and import-time resolution of a template task's defaultAssignee. No I/O — the
// /templates/{id}/import handler reads the template + target project and calls these.

function isoLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// n WORKING days (Mon–Fri) from startStr. Day 0 = the start date itself. Holidays are ignored
// (a task may land on one — accepted). A null/undefined offset returns null → imports blank.
export function addWorkingDays(startStr, n) {
  if (n === null || n === undefined || !startStr) return null;
  const d = new Date(startStr + "T00:00:00");
  let remaining = Math.abs(n);
  const step = n < 0 ? -1 : 1;
  while (remaining > 0) {
    d.setDate(d.getDate() + step);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) remaining--; // count weekdays only
  }
  return isoLocal(d);
}

// A template task's defaultAssignee is discriminated and includes a `role` kind that must NOT
// be flattened to a user id at authoring time — the PM/owner differs per client, so it resolves
// HERE, at import, against the target project row.
export function validateTemplateAssignee(raw) {
  const a = raw || {};
  switch (a.type) {
    case "user": return a.userId ? { type: "user", userId: a.userId } : { type: "unassigned" };
    case "role": return (a.role === "projectManager" || a.role === "accountOwner") ? { type: "role", role: a.role } : { type: "unassigned" };
    case "client": return { type: "client" };
    case "unassigned": return { type: "unassigned" };
    default: return { type: "unassigned" };
  }
}

// Resolve a template task's defaultAssignee to a concrete task assignee at import.
// project = target Ops Planner row (for `role`); clientId = target client (null = internal).
export function resolveTemplateAssignee(defaultAssignee, { project, clientId } = {}) {
  const a = validateTemplateAssignee(defaultAssignee);
  switch (a.type) {
    case "user": return { type: "user", userId: a.userId };
    case "role": {
      const uid = a.role === "accountOwner"
        ? (project?.accountOwner ?? project?.projectOversight ?? null) // legacy fallback
        : (project?.projectManager ?? null);
      return uid ? { type: "user", userId: uid } : { type: "unassigned" };
    }
    case "client":
      return clientId ? { type: "client", clientId } : { type: "unassigned" }; // meaningless internally
    default:
      return { type: "unassigned" };
  }
}

// Which template tasks actually get stamped at import. Tasks flagged `optional` are skipped
// unless the importer opts in. Tasks with no `optional` field are always included (legacy-safe).
export function selectImportTasks(tasks, includeOptional) {
  const all = Array.isArray(tasks) ? tasks : [];
  return includeOptional ? all : all.filter((t) => t?.optional !== true);
}

function toOffset(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

// Validate/normalize a template on create/update. A template is one objective + its tasks.
// Returns { ok:true, value } | { ok:false, status, error }.
export function normalizeTemplate(input, by, nowIso, genId) {
  if (!input || typeof input !== "object") return { ok: false, status: 400, error: "missing request body" };
  const name = typeof input.name === "string" ? input.name.trim() : "";
  if (!name) return { ok: false, status: 400, error: "template name is required" };
  const rawTasks = Array.isArray(input.tasks) ? input.tasks : [];
  const tasks = [];
  for (const t of rawTasks) {
    const tn = typeof t?.name === "string" ? t.name.trim() : "";
    if (!tn) return { ok: false, status: 422, error: "every template task needs a name" };
    tasks.push({
      name: tn,
      instructions: typeof t.instructions === "string" ? t.instructions : "",
      startOffsetDays: toOffset(t.startOffsetDays),
      dueOffsetDays: toOffset(t.dueOffsetDays),
      defaultAssignee: validateTemplateAssignee(t.defaultAssignee),
      optional: t.optional === true, // additive; absent/false = always imported
      private: t.private === true, // imported task hidden from client updates
    });
  }
  return {
    ok: true,
    value: {
      id: input.id || genId(),
      name,
      instructions: typeof input.instructions === "string" ? input.instructions : "",
      private: input.private === true, // imported objective (and so its tasks) hidden from client updates
      tasks,
      createdBy: input.createdBy || by || "system",
      createdAt: input.createdAt || nowIso,
      updatedAt: nowIso,
    },
  };
}
